// Shared display helpers for the Profit & Margin pages.
//
// Absent is rendered as a dash, never as zero. A blank profit means the seller
// has not entered a cost; showing 0 there would be a claim we cannot support.
import React from 'react';
import styles from './ProfitMargin.module.css';

export const Blank = () => <span className={styles.blank}>—</span>;

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const inrPaise = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

// Whole rupees for totals, paise for per-unit figures where the difference matters.
export const money = (value, { paise = false } = {}) => {
  if (value === null || value === undefined) return <Blank />;
  const formatted = paise ? inrPaise.format(value) : inr.format(Math.round(value));
  return `₹${formatted}`;
};

export const percent = (value) =>
  value === null || value === undefined ? <Blank /> : `${value.toFixed(1)}%`;

export const count = (value) =>
  value === null || value === undefined ? <Blank /> : inr.format(value);

// Loss is coloured; a blank stays blank rather than reading as break-even.
export const Money = ({ value, paise = false, signed = false }) => {
  if (value === null || value === undefined) return <Blank />;
  const className = signed && value < 0 ? styles.loss : undefined;
  return <span className={className}>{money(value, { paise })}</span>;
};

export const Percent = ({ value, signed = false }) => {
  if (value === null || value === undefined) return <Blank />;
  const className = signed && value < 0 ? styles.loss : undefined;
  return <span className={className}>{value.toFixed(1)}%</span>;
};

export const SEVERITY_CLASS = {
  CRITICAL: styles.badgeCritical,
  WARNING: styles.badgeWarn,
  INFO: styles.badgeInfo,
};

// Short labels for the table's findings marker. The finding's own title is used
// everywhere else.
const FINDING_LABEL = {
  A1: 'Below breakeven',
  A2: 'Below floor',
  A3: 'Discount cost',
  B1: 'Cost rose',
  B2: 'Thin headroom',
  B3: 'Freight',
  B4: 'Stale cost',
  C2: 'Fulfilment fee',
  D1: 'Returns',
  D2: 'Return rate',
  D3: 'Unsellable',
  D4: 'Returns loss',
  F2: 'Fee share',
  F3: 'Storage',
  F4: 'Long-term storage',
  F5: 'Referral rate',
  FEE_ABOVE_ESTIMATE: 'Fee overcharge',
  G1: 'No cost',
  G2: 'Estimated cost',
  G3: 'Unsellable % not set',
  H1: 'Idle stock',
};

// Marks a figure built on the percentage-of-price default, so estimated and
// actual never look alike, without hovering.
export const EstBadge = ({ show }) => (show
  ? <span className={`${styles.badge} ${styles.badgeWarn}`}>est.</span>
  : null);

export const FindingBadges = ({ findings }) => {
  if (!findings?.length) return <Blank />;
  return (
    <span className={styles.badgeRow}>
      {findings.map((finding) => (
        <span
          key={finding.code}
          className={`${styles.badge} ${SEVERITY_CLASS[finding.severity] || styles.badgeNeutral}`}
        >
          {FINDING_LABEL[finding.code] || finding.code}
        </span>
      ))}
    </span>
  );
};

// A period whose settlements may still be arriving is labelled, so a partial
// period is not read as a collapse in trading.
export const MaturityBadge = ({ maturity }) =>
  maturity === 'MATURING'
    ? <span className={`${styles.badge} ${styles.badgeWarn}`}>still settling</span>
    : <span className={`${styles.badge} ${styles.badgeOk}`}>settled</span>;

export const formatDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// Sync times arrive in UTC without a zone marker, which the browser would read
// as local time. Shown in IST, as the PRD requires.
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/;

export const formatDateTime = (iso) => {
  if (!iso) return 'never';
  const date = new Date(HAS_ZONE.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  })} IST`;
};

// Today and the first of the month, as the date inputs want them.
export const todayIso = () => new Date().toISOString().slice(0, 10);

export const monthStartIso = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString().slice(0, 10);
};
